import LoggerFactory from 'utilities/loggerv2/loggerFactory';
import ErrorCodes from 'utilities/loggerv2/errorCodes';
import { SKYSCAPE_BASKET_ERROR_CODES } from 'constants/basket';
import { BasketAddItemOptions } from 'types/BasketTypes';
import { BasketServiceInterface } from './interface/BasketServiceInterface';
import { startSpan, endSpan, failSpan } from '../../utilities/loggerv2/performanceSpans';

function formatError(error: unknown, defaultCode = 'UNKNOWN_ERROR'): Error {
  const errorObj = error instanceof Error ? error : new Error(String(error || 'Unknown error'));

  if (
    !Object.prototype.hasOwnProperty.call(errorObj, 'code') ||
    (errorObj as any).code === undefined ||
    (errorObj as any).code === null
  ) {
    (errorObj as any).code = defaultCode;
  }

  return errorObj;
}

export default class BasketService {
  private basketService: BasketServiceInterface;

  private loggerPromise: Promise<any>;

  private defaultErrorMessage: string;

  constructor(basketService: BasketServiceInterface) {
    this.basketService = basketService;
    this.loggerPromise = LoggerFactory.getLogger('Add to basket service');
    this.defaultErrorMessage = 'An error occurred while adding the item to the basket. Please try again later.';
  }

  async addItem(options: BasketAddItemOptions): Promise<string> {
    const spanId = startSpan('addToBasket', {
      productSku: options.item.variantId,
      hasPersonalisation: !!options.item.personalisation,
      hasWarranty: !!options.item.warrantyOptionNumber,
      hasAddons: !!(options.item.selectedAddOnServices && options.item.selectedAddOnServices.length > 0),
    });

    try {
      const response = await this.basketService.addItem(options);

      // TODO: These are the first "SKYSCAPE" errors we are receiving.
      // When we move off ATG, this file will need to be refactored to handle new error structures accordingly.
      const errorResponse = response?.errors?.find((error) => SKYSCAPE_BASKET_ERROR_CODES.includes(error.key));

      if (errorResponse && errorResponse.errors && errorResponse.errors.length > 0) {
        const errorMessage = errorResponse.errors[0];
        const errorObj = new Error(errorMessage);

        Object.defineProperty(errorObj, 'code', {
          value: errorResponse.key,
          writable: true,
          enumerable: true,
        });

        (await this.loggerPromise).logWithCode(ErrorCodes.BASKET_SERVICE_ERROR, errorObj);

        failSpan(spanId, {
          errorType: errorResponse.key,
          status: response.status,
          statusText: response.statusText,
        });

        return errorMessage;
      }

      if (!response.success) {
        const errorMessage = `HTTP error ${response.status ? response.status : 'Unknown'}: ${response.statusText ? response.statusText : 'Unknown'}`;
        const errorObj = new Error(errorMessage);

        Object.defineProperty(errorObj, 'code', {
          value: response.status ? response.status.toString() : 'UNKNOWN_HTTP_ERROR',
          writable: true,
          enumerable: true,
        });

        (await this.loggerPromise).logWithCode(
          ErrorCodes.BASKET_SERVICE_ERROR || {
            code: 'PRDFE-06001',
            message: 'HTTP error in basket service',
            level: 3,
            category: 'BASKET',
            description: 'An HTTP error occurred in the basket service',
          },
          errorObj,
        );

        failSpan(spanId, {
          errorType: ErrorCodes.BASKET_SERVICE_ERROR.code,
          status: response.status || 'Unknown',
          statusText: response.statusText || 'Unknown',
        });

        return this.defaultErrorMessage;
      }

      if (response.formError || (response.formExceptions && response.formExceptions.length > 0)) {
        const errorMessages = response.formExceptions
          ? response.formExceptions.map((ex: any) => ex.localizedMessage || 'Unknown error').join(', ')
          : 'Unknown API error';

        const errorCodes = response.formExceptions
          ? response.formExceptions.map((ex: any) => ex.errorCode || 'unknown')
          : ['unknown'];

        const errorObj = new Error(errorMessages);

        (errorObj as any).code = errorCodes[0] || 'UNKNOWN_API_ERROR';

        (await this.loggerPromise).logWithCode(ErrorCodes.BASKET_API_ERROR, errorObj);

        failSpan(spanId, {
          errorType: 'API_ERROR',
          errorCodes,
          messages: errorMessages,
          formError: response.formError || false,
          concurrentUpdate: response.concurrentUpdate || false,
        });

        return this.defaultErrorMessage;
      }

      endSpan(spanId, { success: true });

      return '';
    } catch (error) {
      const errorObj = formatError(error, 'UNKNOWN_ERROR');

      (await this.loggerPromise).logWithCode(ErrorCodes.BASKET_SERVICE_ERROR, errorObj);

      failSpan(spanId, {
        errorType: 'SERVICE_ERROR',
        message: errorObj.message || 'Unknown error',
        stack: errorObj.stack || '',
        errorCode: (errorObj as any).code || 'UNKNOWN_ERROR',
      });

      return this.defaultErrorMessage;
    }
  }
}
